import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle2, Clock, MessageSquare, PhoneCall, Filter } from 'lucide-react';
import { api } from '../../api/client';
import { showToast } from '../../components/common/Toast';
import { formatUzPhone } from '../../utils/helpers';

function formatRecipient(recipient = '') {
  if (!recipient) return '—';
  if (recipient === 'ADMIN') return 'Bosh Admin (Tizim)';
  if (recipient === 'STAFF') return 'Xodimlar';
  if (/^\+?998\d+$/.test(recipient.replace(/\s+/g, '')) || /^\d{9,12}$/.test(recipient)) {
    return formatUzPhone(recipient);
  }
  return recipient;
}

function getNotificationBadge(title = '', channel = '') {
  const clean = (title || '').replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|\p{Extended_Pictographic}/gu, '').trim();
  const lower = clean.toLowerCase();
  if (lower.includes('soat') || lower.includes('kun') || lower.includes('eslatma') || lower.includes('qold')) {
    return {
      icon: <Clock className="w-3.5 h-3.5 text-amber-600 mr-2 shrink-0" />,
      title: clean,
    };
  }
  if (channel === 'SMS' || lower.includes('sms')) {
    return {
      icon: <MessageSquare className="w-3.5 h-3.5 text-blue-600 mr-2 shrink-0" />,
      title: clean,
    };
  }
  if (lower.includes('tasdiq') || lower.includes('qabul')) {
    return {
      icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mr-2 shrink-0" />,
      title: clean,
    };
  }
  return {
    icon: <Bell className="w-3.5 h-3.5 text-slate-500 mr-2 shrink-0" />,
    title: clean,
  };
}

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ALL'); // 'ALL' | 'SMS' | 'IN_APP'

  const loadNotifications = () => {
    setLoading(true);
    api.get('/audit-logs/notifications')
      .then((res) => {
        if (res?.success) setNotifications(res.notifications || []);
      })
      .catch((err) => showToast(err.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 'SMS') return n.channel === 'SMS';
    if (activeTab === 'IN_APP') return n.channel === 'IN_APP';
    return true;
  });

  const smsCount = notifications.filter((n) => n.channel === 'SMS').length;
  const inAppCount = notifications.filter((n) => n.channel === 'IN_APP').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-lg font-bold text-slate-900">
            Bildirishnomalar & SMS Jurnali ({notifications.length})
          </h2>
          <p className="text-xs text-slate-500">
            Mijozlarga va ma’muriyatga yuborilgan barcha SMS xabarnomalar va tizim eslatmalari tarixi
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 text-xs shadow-2xs">
          <button
            type="button"
            onClick={() => setActiveTab('ALL')}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors cursor-pointer ${
              activeTab === 'ALL'
                ? 'bg-[#091322] text-[#c5a880]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Barchasi ({notifications.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('SMS')}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors cursor-pointer inline-flex items-center ${
              activeTab === 'SMS'
                ? 'bg-[#091322] text-[#c5a880]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1" />
            SMS Xabarlar ({smsCount})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('IN_APP')}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors cursor-pointer inline-flex items-center ${
              activeTab === 'IN_APP'
                ? 'bg-[#091322] text-[#c5a880]'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Bell className="w-3.5 h-3.5 mr-1" />
            Tizim ({inAppCount})
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4 whitespace-nowrap">Qabul qiluvchi / Telefon</th>
                <th className="py-3 px-4 whitespace-nowrap">Kanal</th>
                <th className="py-3 px-4">Mavzu va Xabar matni</th>
                <th className="py-3 px-4 whitespace-nowrap">Bron ID</th>
                <th className="py-3 px-4 whitespace-nowrap">Holat</th>
                <th className="py-3 px-4 whitespace-nowrap text-right">Vaqt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">Yuklanmoqda...</td>
                </tr>
              ) : filteredNotifications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-slate-400">
                    Hozircha xabarnomalar mavjud emas
                  </td>
                </tr>
              ) : (
                filteredNotifications.map((n) => {
                  const badge = getNotificationBadge(n.title, n.channel);
                  const isSms = n.channel === 'SMS';
                  return (
                    <tr key={n.id} className="hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 font-mono font-medium text-slate-800 whitespace-nowrap tabular-nums">
                        {formatRecipient(n.recipient)}
                        {n.appointment?.clientName && (
                          <div className="text-[10px] text-slate-500 font-sans mt-0.5">
                            {n.appointment.clientName}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                          isSms
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'bg-slate-100 text-slate-700 border border-slate-200'
                        }`}>
                          {isSms ? (
                            <MessageSquare className="w-3 h-3 mr-1 text-blue-600" />
                          ) : (
                            <Bell className="w-3 h-3 mr-1 text-amber-600" />
                          )}
                          {isSms ? 'SMS Xabar' : 'Tizim'}
                        </span>
                      </td>
                      <td className="py-3 px-4 max-w-md">
                        <div className="flex items-center text-slate-900 font-medium">
                          {badge.icon}
                          <span className="truncate">{badge.title}</span>
                        </div>
                        {n.message && (
                          <p className="text-[11px] text-slate-600 mt-1 line-clamp-2 leading-relaxed">
                            {n.message.replace(/<[^>]*>?/gm, '')}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono font-bold text-slate-800 whitespace-nowrap tabular-nums">
                        {n.appointment?.bookingReference || '—'}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          n.status === 'SENT'
                            ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                            : 'bg-rose-50 text-rose-800 border border-rose-200'
                        }`}>
                          {n.status === 'SENT' ? 'Yuborildi' : 'Xatolik'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500 text-[11px] font-mono whitespace-nowrap tabular-nums text-right">
                        {new Date(n.createdAt).toLocaleString('uz-UZ', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
